import { isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { AuthService } from '@/app/core/auth/auth.service';
import { AccountUiPreferencesDto, AccountUiPreferencesService } from '@/app/core/services/account-ui-preferences.service';
import { themeApplicator } from './theme.constants';

export interface LayoutConfig {
    preset: string;
    primary: string;
    surface: string | undefined | null;
    darkTheme: boolean;
    menuMode: string;
    menuTheme: string;
    cardStyle: string;
}

interface LayoutState {
    staticMenuInactive: boolean;
    overlayMenuActive: boolean;
    configSidebarVisible: boolean;
    mobileMenuActive: boolean;
    sidebarExpanded: boolean;
    menuHoverActive: boolean;
    activePath: string | null;
    anchored: boolean;
}

const STORAGE_KEY = 'expresserp_layout_config';

const DEFAULT_CONFIG: LayoutConfig = {
    preset: 'Aura',
    primary: 'blue',
    surface: null,
    darkTheme: true,
    menuMode: 'static',
    menuTheme: 'dark',
    cardStyle: 'transparent'
};

@Injectable({
    providedIn: 'root'
})
/* v8 ignore start */
export class LayoutService {
/* v8 ignore stop */
    private readonly platformId = inject(PLATFORM_ID);
    private readonly router = inject(Router);
    private readonly preferencesService = inject(AccountUiPreferencesService);
    private readonly authService = inject(AuthService);

    layoutConfig = signal<LayoutConfig>(DEFAULT_CONFIG);

    layoutState = signal<LayoutState>({
        staticMenuInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        mobileMenuActive: false,
        sidebarExpanded: false,
        menuHoverActive: false,
        activePath: null,
        anchored: false
    });

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);
    isSlim = computed(() => this.layoutConfig().menuMode === 'slim');
    isHorizontal = computed(() => this.layoutConfig().menuMode === 'horizontal');
    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');
    isCompact = computed(() => this.layoutConfig().menuMode === 'compact');
    isStatic = computed(() => this.layoutConfig().menuMode === 'static');
    isReveal = computed(() => this.layoutConfig().menuMode === 'reveal');
    isDrawer = computed(() => this.layoutConfig().menuMode === 'drawer');

    hasOverlaySubmenu = computed(() => this.isSlim() || this.isCompact() || this.isHorizontal());
    hasOpenOverlay = computed(() => this.layoutState().overlayMenuActive || this.hasOpenOverlaySubmenu());
    hasOpenOverlaySubmenu = computed(() => {
        return this.hasOverlaySubmenu() && !!this.layoutState().activePath;
    });

    isSidebarStateChanged = computed(() => {
        const layoutConfig = this.layoutConfig();
        return layoutConfig.menuMode === 'horizontal' || layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus';
    });

    private initialized = false;
    private previousMenuMode: string | undefined = undefined;

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.initLocalConfig();
        }

        effect(() => {
            const current = this.authService.session();
            if (!isPlatformBrowser(this.platformId) || !current || current.expiresAt <= Date.now()) {
                return;
            }
            untracked(() => this.loadPreferencesFromBackend());
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                this.toggleDarkMode(config);
                return;
            }

            this.handleDarkModeTransition(config);
        });

        effect(() => {
            this.updateMenuState();
        });
    }

    private initLocalConfig() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved) as LayoutConfig;
                this.layoutConfig.set(parsed);
                themeApplicator.applyFullTheme(parsed.preset, parsed.primary, parsed.surface);
            }
        } catch {
            // Ignore parse errors and fallback to default
        }
    }

    loadPreferencesFromBackend() {
        this.preferencesService.getPreferences().subscribe({
            next: (dto) => {
                const backendConfig: LayoutConfig = {
                    preset: dto.preset || 'Aura',
                    primary: dto.primary || 'blue',
                    surface: dto.surface,
                    darkTheme: dto.dark_theme ?? true,
                    menuMode: dto.menu_mode || 'static',
                    menuTheme: dto.menu_theme || 'dark',
                    cardStyle: dto.card_style || 'transparent'
                };
                this.layoutConfig.set(backendConfig);
                this.saveToLocalStorage(backendConfig);
                themeApplicator.applyFullTheme(backendConfig.preset, backendConfig.primary, backendConfig.surface);
            },
            error: () => {
                // If backend is not reached yet (or unauthenticated), keep local config
            }
        });
    }

    savePreferencesToBackend(config?: LayoutConfig): Observable<AccountUiPreferencesDto> {
        const current = config || this.layoutConfig();
        const payload: Partial<AccountUiPreferencesDto> = {
            preset: current.preset,
            primary: current.primary,
            surface: current.surface,
            dark_theme: current.darkTheme,
            menu_mode: current.menuMode,
            menu_theme: current.menuTheme,
            card_style: current.cardStyle
        };

        return this.preferencesService.savePreferences(payload).pipe(
            tap((savedDto) => {
                const updated: LayoutConfig = {
                    preset: savedDto.preset,
                    primary: savedDto.primary,
                    surface: savedDto.surface,
                    darkTheme: savedDto.dark_theme,
                    menuMode: savedDto.menu_mode,
                    menuTheme: savedDto.menu_theme,
                    cardStyle: savedDto.card_style
                };
                this.layoutConfig.set(updated);
                this.saveToLocalStorage(updated);
                themeApplicator.applyFullTheme(updated.preset, updated.primary, updated.surface);
            })
        );
    }

    updateConfig(changes: Partial<LayoutConfig>, persistToBackend: boolean = false) {
        const updated: LayoutConfig = {
            ...this.layoutConfig(),
            ...changes
        };
        this.layoutConfig.set(updated);
        this.saveToLocalStorage(updated);

        if (changes.preset || changes.primary || changes.surface !== undefined) {
            themeApplicator.applyFullTheme(updated.preset, updated.primary, updated.surface);
        }

        if (persistToBackend && this.authService.isAuthenticated()) {
            this.savePreferencesToBackend(updated).subscribe();
        }
    }

    private saveToLocalStorage(config: LayoutConfig) {
        if (isPlatformBrowser(this.platformId)) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
            } catch {
                // Local storage disabled or quota exceeded
            }
        }
    }

    private updateMenuState() {
        const menuMode = this.layoutConfig().menuMode;
        if (this.previousMenuMode === undefined) {
            this.previousMenuMode = menuMode;
            return;
        }

        if (this.previousMenuMode === menuMode) {
            return;
        }

        this.previousMenuMode = menuMode;

        const isOverlaySubmenu = menuMode === 'slim' || menuMode === 'slim-plus' || menuMode === 'horizontal' || menuMode === 'compact';

        this.layoutState.update((prev) => ({
            ...prev,
            staticMenuInactive: false,
            overlayMenuActive: false,
            mobileMenuActive: false,
            sidebarExpanded: false,
            menuHoverActive: false,
            anchored: false,
            activePath: this.isDesktop() ? (isOverlaySubmenu ? null : this.router.url) : prev.activePath
        }));
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        const supportsViewTransition = 'startViewTransition' in document;

        if (supportsViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
        }
    }

    private startViewTransition(config: LayoutConfig): void {
        (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });
    }

    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    toggleMenu() {
        if (this.isDesktop()) {
            if (this.layoutConfig().menuMode === 'static') {
                this.layoutState.update((prev) => ({ ...prev, staticMenuInactive: !prev.staticMenuInactive }));
            }

            if (this.layoutConfig().menuMode === 'overlay') {
                this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
            }
        } else {
            this.layoutState.update((prev) => ({ ...prev, mobileMenuActive: !prev.mobileMenuActive }));
        }
    }

    changeMenuMode(mode: string) {
        this.updateConfig({ menuMode: mode }, true);

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, activePath: this.hasOverlaySubmenu() ? null : this.router.url }));
        }
    }

    toggleConfigSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            configSidebarVisible: !prev.configSidebarVisible
        }));
    }

    isDesktop() {
        return window.innerWidth > 991;
    }
}
